import {getChart} from "./poh-service.js";

export function getDensityAltitude(pressureAltitude, temperature) {
    const stdTemperature = 15 - 1.98 * pressureAltitude / 1000;
    return pressureAltitude + (temperature - stdTemperature) * 120;
}

export function getTas(aircraftType, temperature, pressureAltitude, power, airborne) {
    if (power === 0) {
        return airborne ? 73 : 0; // idle - glide if airborne
    } 

    const densityAltitude = getDensityAltitude(pressureAltitude, temperature);

    // const cruiseTas = getChart(aircraftType, "CRUISE_TAS").performance.find(entry => entry.power === power);
    const perfChart = getChart(aircraftType, "CRUISE_TAS");
    const DENSITY_ALT_PERF = perfChart.performance.map(entry => ({
        param1: parseFloat(entry.power),
        param2: parseFloat(entry.densityAlt),
        result: parseFloat(entry.tas)
    }));

    let result = interpolateMatrix(DENSITY_ALT_PERF, power/100, densityAltitude);

    if (densityAltitude > 8000) {
        const result_full_power = interpolateMatrix(DENSITY_ALT_PERF, 1, densityAltitude);
        result = Math.min(result, result_full_power);
    }

    return result;
}

export function getPower(tas, altitude, pitch) {
    if (pitch === 1) {
        return 100;
    } else if (pitch === -1) {
        return 0;
    } else if (tas >= 115) {
        return 100;
    } else if (tas >= 108) {
        return 75;
    } else if (tas >= 97) {
        return 65;
    } else if (tas >= 73) {
        return 55;
    } else if (altitude > 0) {
        return 0;
    } else {
        return 0;
    }
}

export function getFuelFlow(aircraftType, currentPower) {
    const perfChart = getChart(aircraftType, "FUEL");
    const FUEL_FLOW_PERF = perfChart.performance.map(entry => ({
        param: parseFloat(entry.power),
        result: parseFloat(entry.fuelFlow)
    }));

    const fuelFlow = interpolateSingle(FUEL_FLOW_PERF, currentPower / 100);
    return fuelFlow;
}

export function getVr() {
    return 60;
}

export function getVy() {
    return 74;
}

export function getVx() {
    return 65;
}

export function getVglide() {
    return 73;
}

export function getVapp() {
    return 90;
}

export function getVref() {
    return 65;
}

export function getAcceleration() {
    return 10; // 10 kts per second
}

export const STANDARD_VS_CLIMB = 700;
export const STANDARD_VS_DESCENT = -700;
export const STANDARD_VS_GLIDE = -1000;

function getClimbRate(aircraftType, temperature, pressureAltitude, weight) {

    const densityAltitude = getDensityAltitude(pressureAltitude, temperature);

    const perfChart = getChart(aircraftType, "CLIMB_RATE");
    const CLIMB_RATE_PERF = perfChart.performance.map(entry => ({
        param1: parseFloat(entry.weight),
        param2: parseFloat(entry.densityAlt),
        result: parseFloat(entry.climbRate)
    }));    

    return interpolateMatrix(CLIMB_RATE_PERF, weight, densityAltitude);
}

export function getVS(aircraftType, temperature, pressureAltitude, weight, pitch, power) {
    if (pitch === 1) {
        return getClimbRate(aircraftType, temperature, pressureAltitude, weight, pitch);
    } else if (pitch === -1) {
        return power === 0 ? STANDARD_VS_GLIDE : STANDARD_VS_DESCENT;
    }
}

export function calculateChange(current, target, rate, timePassed, mod = 0) {
    if (mod !== 0 && Math.sign(rate) !== Math.sign(target - current)) {
        if (target > current) {
            current += mod;
        } else {
            target += mod;
        }
    }
    const delta = target - current;
    const timedDelta = rate * timePassed;
    return Math.min(Math.abs(delta), Math.abs(timedDelta)) * Math.sign(rate);
}

const EMPTY_WEIGHTS = { // in lbs
    "P28A-161": 1548,
    "P28A-181": 1616,
    "P28R-201": 1664,
    "P28U-201": 1929
}

const MAX_WEIGHTS = { // in lbs
    "P28A-161": 2325,
    "P28A-181": 2550,
    "P28R-201": 2750,
    "P28U-201": 2900
}

export function getEmptyWeight(type) {
    return EMPTY_WEIGHTS[type];
}

export function getMaxWeight(type) {
    return MAX_WEIGHTS[type];
}

const STANDARD_POB_WEIGHT = 200; // in lbs

export function getPayloadWeight(pob) {
    return pob * STANDARD_POB_WEIGHT;
}

export const FUEL_TYPES = {
    "P28A-161": "AVGAS",
    "P28A-181": "AVGAS",
    "P28R-201": "AVGAS",
    "P28U-201": "AVGAS"
}

export const FUEL_MAX_QTY = {
    "P28A-161": 48,
    "P28A-181": 48,
    "P28R-201": 72,
    "P28U-201": 72
}

export const AVGAS_SPECIFIC_GRAVITY = 6.04; // lbs per gallon

export function getFuelWeight(fuel) {
    return fuel * AVGAS_SPECIFIC_GRAVITY;
}

function interpolateSingle(relationshipTable, param) {
    const sortedTable = relationshipTable.sort((a, b) => a.param - b.param);

    let lowerEntry = sortedTable[0];
    let upperEntry = sortedTable[sortedTable.length - 1];

    // Find the bracketing values
    for (let i = 0; i < sortedTable.length - 1; i++) {
        if (sortedTable[i].param <= param && sortedTable[i + 1].param >= param) {
            lowerEntry = sortedTable[i];
            upperEntry = sortedTable[i + 1];
            break;
        }
    }

    // If param is outside the range, return the nearest value
    if (param < lowerEntry.param) {
        return lowerEntry.result;
    } else if (param > upperEntry.param) {
        return upperEntry.result;
    }

    // Linear interpolation
    return lowerEntry.result +
        ((param - lowerEntry.param) * (upperEntry.result - lowerEntry.result)) /
        (upperEntry.param - lowerEntry.param);
}

function interpolateMatrix(relationshipMatrix, param1, param2) {
    const param1Values = [...new Set(relationshipMatrix.map(row => row.param1))].sort((a, b) => a - b);

    let lowerParam1 = param1Values[0];
    let upperParam1 = param1Values[param1Values.length - 1];

    for (let i = 0; i < param1Values.length - 1; i++) {
        if (param1Values[i] <= param1 && param1Values[i + 1] >= param1) {
            lowerParam1 = param1Values[i];
            upperParam1 = param1Values[i + 1];
            break;
        }
    }

    if (param1 < lowerParam1) {
        upperParam1 = lowerParam1;
    } else if (param1 > upperParam1) {
        lowerParam1 = upperParam1;
    }

    const interpolateParam2 = (param1) => {
        const rows = relationshipMatrix.filter(row => row.param1 === param1);

        rows.sort((a, b) => a.param2 - b.param2);

        let lowerParam2 = rows[0];
        let upperParam2 = rows[rows.length - 1];

        for (let i = 0; i < rows.length - 1; i++) {
            if (rows[i].param2 <= param2 && rows[i + 1].param2 >= param2) {
                lowerParam2 = rows[i];
                upperParam2 = rows[i + 1];
                break;
            }
        }

        if (param2 < lowerParam2.param2) {
            return lowerParam2.result; // Use the nearest lower value
        } else if (param2 > upperParam2.param2) {
            return upperParam2.result; // Use the nearest upper value
        }

        return lowerParam2.result +
            ((param2 - lowerParam2.param2) * (upperParam2.result - lowerParam2.result)) /
            (upperParam2.param2 - lowerParam2.param2);
    };

    const lowerResult = interpolateParam2(lowerParam1);
    const upperResult = interpolateParam2(upperParam1);

    let result = lowerResult
    if (upperParam1 !== lowerParam1) {
        result += ((param1 - lowerParam1) * (upperResult - lowerResult)) / (upperParam1 - lowerParam1);
    }

    return result
}

export function convertToMeters(distanceFeet) {
    return distanceFeet / 3.28;
}

export function convertToFeet(distanceMeters) {
    return distanceMeters * 3.28;
}

function _getPerfDistance(distanceType, aircraftType, temperature, pressureAltitude, weight, wind) {
    const perfChart = getChart(aircraftType, distanceType);

    const DENSITY_ALT_PERF = perfChart.performance.map(entry => ({
        param1: parseFloat(entry.pressureAlt),
        param2: parseFloat(entry.temperature),
        result: parseFloat(entry.distance)
    }));

    let distance = interpolateMatrix(DENSITY_ALT_PERF, pressureAltitude, temperature);

    if (weight < MAX_WEIGHTS[aircraftType]) {
        // Adjust for weight
        const WEIGHT_ADJ = perfChart.weightAdjustment.map(entry => ({
            param1: parseFloat(entry.distance),
            param2: parseFloat(entry.weight),
            result: parseFloat(entry.distanceAdj)
        }));
        distance = interpolateMatrix(WEIGHT_ADJ, distance, weight);
    }

    if (wind > 0) { // headwind
        const HEADWIND_ADJ = perfChart.headwindAdjustment.map(entry => ({
            param1: parseFloat(entry.distance),
            param2: parseFloat(entry.headwind),
            result: parseFloat(entry.distanceAdj)
        }));
        distance = interpolateMatrix(HEADWIND_ADJ, distance, wind);
    } else if (wind < 0) { // tailwind
        const TAILWIND_ADJ = perfChart.tailwindAdjustment.map(entry => ({
            param1: parseFloat(entry.distance),
            param2: parseFloat(entry.tailwind),
            result: parseFloat(entry.distanceAdj)
        }));
        distance = interpolateMatrix(TAILWIND_ADJ, distance, Math.abs(wind));
    }

    return distance;
}

export function getTORR(aircraftType, temperature, pressureAltitude, weight, wind) {
    return _getPerfDistance("TORR", aircraftType, temperature, pressureAltitude, weight, wind);
}

export function getTODR(aircraftType, temperature, pressureAltitude, weight, wind) {
    return _getPerfDistance("TODR", aircraftType, temperature, pressureAltitude, weight, wind);
}

export function getLDR(aircraftType, temperature, pressureAltitude, weight, wind) {
    return _getPerfDistance("LDR", aircraftType, temperature, pressureAltitude, weight, wind);
}